import axiosInstance from 'api/axiosInstance';
import appI18nInstance from 'i18n';
import { stringify } from 'query-string';
import type {
  ExpenseCreateRecurrenceData,
  ExpensePaymentStatusType,
  IExpenseV2,
  PartialIExpenseV2,
} from 'types/expenses.types';
import type { ExpensesV2FilterResponse, Period } from 'types/global.types';
import { Transaction } from 'types/transactions.types';
import { isExpert } from 'utils/constants';
import { onCreateExpenseGTM } from 'utils/GTM';
import { FIVE_MINUTES, TWO_MINUTES } from 'utils/time';
import appQueryClient from 'api/appQueryClient';
import { UserCacheKeys } from 'types/cacheKeys.types';

export type getExpensesParams = {
  page: number;
  source?: string;
  reviewed?: boolean;
  attachment?: string;
  payment?: string;
  expand?: string[];
  fields?: string;
  sort?: string;

  perPage?: number;
  per_page?: number;

  sufficientlyDocumented?: boolean;
  sufficiently_documented?: boolean;

  isInvoice?: boolean;
  is_invoice?: boolean;

  text?: string;
  search?: string;

  categoryIds?: string[];
  categories?: string[];

  isValidated?: boolean;
  is_validated?: boolean;

  isCreditNote?: boolean;
  is_credit_note?: boolean;

  dateRange?: string[];
  date_range?: string[];

  periods?: string[];
  vat_period?: string[];

  taxPeriods?: string[];
  tax_period?: string[];
};

const service = '/v3/expenses';

export const createExpense = (filePath: string, isFake?: boolean) => {
  return axiosInstance
    .post<any, { expense: IExpenseV2 }>(
      `${service}/from-file?cache=${Date.now()}`,
      {
        file_name: filePath.split('/').pop(),
        file_path: filePath,
        is_fake: isFake,
      },
      {
        timeout: FIVE_MINUTES,
      },
    )
    .then((data) => {
      onCreateExpenseGTM();
      appQueryClient.invalidateQueries({
        queryKey: [UserCacheKeys.FeatureCounts],
      });
      return data.expense;
    });
};

export const getExpenseDuplicates = (
  expenseId: string,
): Promise<(IExpenseV2 & { duplicateReason?: string })[]> => {
  return axiosInstance
    .get<
      any,
      {
        expenses: IExpenseV2[];
        reasons: { reason: string; expense_id: string }[];
      }
    >(
      `${service}/${expenseId}/duplicates?${stringify({
        perPage: 10,
        page: 1,
        lang: appI18nInstance.language,
        expand: ['taxLock'],
      })}`,
      {
        timeout: TWO_MINUTES,
      },
    )
    .then((data) => {
      const reasons = {} as Record<string, string>;
      data.reasons.forEach((data) => {
        reasons[data.expense_id] = data.reason;
      });
      return data.expenses.map((dup) => ({
        ...dup,
        duplicateReason: reasons[dup._id],
      }));
    });
};

export const getExpenses = (query: getExpensesParams) => {
  return axiosInstance
    .get<
      any,
      {
        expenses: IExpenseV2[];
        paging: {
          per_page: number;
          page: number;
          total_pages: number;
          total_count: number;
        };
      }
    >(
      `${service}?${stringify(
        {
          ...query,
          perPage: undefined,
          per_page: query.per_page ?? query.perPage,

          sufficientlyDocumented: undefined,
          sufficiently_documented:
            query.sufficiently_documented ?? query.sufficientlyDocumented,

          isInvoice: undefined,
          is_invoice: query.is_invoice ?? query.isInvoice,

          text: undefined,
          search: query.search ?? query.text,

          categoryIds: undefined,
          categories: query.categories ?? query.categoryIds,

          isValidated: undefined,
          is_validated: query.is_validated ?? query.isValidated,

          isCreditNote: undefined,
          is_credit_note: query.is_credit_note ?? query.isCreditNote,

          dateRange: undefined,
          date_range: query.date_range ?? query.dateRange,

          periods: undefined,
          vat_period: query.vat_period ?? query.periods,

          taxPeriods: undefined,
          tax_period: query.tax_period ?? query.taxPeriods,

          lang: appI18nInstance.language,
        },
        {
          skipEmptyString: true,
          arrayFormat: 'comma',
        },
      )}`,
    )
    .then((data) => ({
      data: data.expenses,
      paging: {
        page: data.paging.page,
        perPage: data.paging.per_page,
        pagesCount: data.paging.total_pages,
        totalCount: data.paging.total_count,
      },
    }));
};

export const getExpenseById = (expenseId: string): Promise<IExpenseV2> =>
  axiosInstance
    .get<any, { expense: IExpenseV2 }>(`${service}/${expenseId}`, {
      params: {
        expand: 'payments.transactions,taxLock,guessed_data,recurrence',
        lang: appI18nInstance.language,
      },
    })
    .then((data) => {
      const expense = data.expense;
      const transactions = (expense?.payments?.transactions || []).filter(
        (t) => !!t,
      ) as unknown as Transaction[];
      return {
        ...expense,
        payments: {
          ...expense.payments,
          transactions: transactions.map((t) => t._id),
        },
        _matchedTransactions: transactions,
      };
    });

export const updateExpenseById = (
  expenseId: string,
  { shouldUpdateTaxStatus, create_recurrence, ...expense }: PartialIExpenseV2,
  options?: {
    update_tax_status?: boolean;
    create_recurrence?: ExpenseCreateRecurrenceData;
  },
) => {
  const data = {
    expense,
    options: {
      update_tax_status: !!shouldUpdateTaxStatus,
      create_recurrence,
      ...options,
    },
  };
  return axiosInstance
    .put<any, { expense: IExpenseV2 }>(`${service}/${expenseId}`, data)
    .then((data) => data.expense);
};

export const addExpense = (
  { shouldUpdateTaxStatus, create_recurrence, ...expense }: PartialIExpenseV2,
  options?: {
    update_tax_status?: boolean;
    create_recurrence?: ExpenseCreateRecurrenceData;
  },
) => {
  const data = {
    expense,
    options: {
      update_tax_status: !!shouldUpdateTaxStatus,
      create_recurrence,
      ...options,
    },
  };
  return axiosInstance
    .post<any, { expense: IExpenseV2 }>(service, data)
    .then((data) => {
      onCreateExpenseGTM();
      appQueryClient.invalidateQueries({
        queryKey: [UserCacheKeys.FeatureCounts],
      });
      return data.expense;
    });
};

export const getExpensesFilters = () =>
  axiosInstance
    .get<
      any,
      {
        filters: ExpensesV2FilterResponse[];
      }
    >(`${service}/filters`, {
      params: {
        lang: appI18nInstance.language,
      },
    })
    .then((data) => ({ data: data.filters }));

export const getExpenseTaxImpact = ({
  shouldUpdateTaxStatus,
  create_recurrence,
  ...expense
}: Partial<IExpenseV2>): Promise<any> => {
  return axiosInstance
    .post(`${service}/tax-impact`, {
      expense,
      options: { tax_impact_requested: true },
    })
    .then(({ data }) => data);
};

export const getExpenseVATCells = ({
  shouldUpdateTaxStatus,
  create_recurrence,
  ...expense
}: PartialIExpenseV2): Promise<{ key: string; value: number }[]> => {
  return axiosInstance
    .post<
      any,
      { data: { total: { vat: { statement: Record<string, number> } } } }
    >(
      `${service}/tax-impact`,
      { expense, options: { tax_impact_requested: false } },
      {
        ignoreToast: true,
      } as any,
    )
    .then(({ data }) => {
      return Object.entries(data.total.vat.statement)
        .filter(([key, value]) => !!value)
        .map(([key, value]) => ({ key, value }));
    });
};

export const patchLockedExpense = (
  expenseId: string,
  body: Partial<
    Pick<
      IExpenseV2,
      'payments' | 'notes' | 'accountantReview' | 'file' | 'file'
    >
  >,
) => {
  return axiosInstance
    .patch<any, { expense: IExpenseV2 }>(`${service}/${expenseId}`, {
      expense: {
        file: body.file?.path
          ? {
              name: body.file.name || body.file.path.split('/').pop(),
              path: body.file.path,
            }
          : null,
        notes: body.notes || null,
        accountantReview: isExpert ? body.accountantReview : undefined,
        payments: {
          cash: !!body.payments?.cash,
          other_accounts: !!body.payments?.other_accounts,
          transactions: body.payments?.transactions || [],
          status: {
            type: body.payments?.status?.type || undefined,
            due_date: body.payments?.status?.due_date || undefined,
          },
        },
      },
    })
    .then((data) => data.expense);
};

export const patchExpense = (
  expenseId: string,
  body: Partial<
    Pick<IExpenseV2, 'payments' | 'notes' | 'accountantReview' | 'file'>
  >,
) => {
  return axiosInstance
    .patch<any, { expense: IExpenseV2 }>(`${service}/${expenseId}`, {
      expense: body,
    })
    .then((data) => data.expense);
};

export const deleteExpenseById = (expenseId: string): Promise<void> =>
  axiosInstance.delete(`${service}/${expenseId}`);

export const requestNextRecurringDate = (data: any) => {
  return axiosInstance.get(`${service}/recurrences/next-date`, {
    params: data,
  });
};

export const pauseRecurrence = (recurrenceId: string): Promise<void> =>
  axiosInstance.post(`${service}/recurrences/${recurrenceId}/pause`);

type BulkUpdateExpensesChanges = {
  payment_status_type?: ExpensePaymentStatusType;
  period?: Period;
};

export const bulkUpdateExpensesV3 = (
  expensesIds: string[],
  changes: BulkUpdateExpensesChanges,
) => {
  return axiosInstance.post(`${service}/bulk`, {
    expenses_ids: expensesIds,
    changes,
  });
};
